import React, { useState, useRef, useEffect } from 'react';
import {
  Camera,
  MapPin,
  RefreshCw,
  Upload,
  CheckCircle2,
  X,
  Sparkles,
  Layers,
  FileCheck,
  Smartphone,
  HardDrive,
  AlertTriangle,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { Account } from '../../types';

interface WatermarkCameraModalProps {
  account: Account;
  visitId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (photoRecord: any) => void;
}

export const WatermarkCameraModal: React.FC<WatermarkCameraModalProps> = ({
  account,
  visitId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { currentUser, captureWatermarkedPhoto, photos } = useSRMS();

  /**
   * How many photos this account already holds.
   *
   * An account keeps its two most recent photos; a third replaces the oldest.
   * The agent is told BEFORE they press the button, because a photo quietly
   * disappearing from an account is exactly the kind of thing that gets
   * reported as data loss six weeks later.
   */
  const MAX_PHOTOS_PER_ACCOUNT = 2;
  const existingPhotoCount = (photos || []).filter(
    (p) => p.accountId === account?.accountId
  ).length;
  const willReplaceOldest = existingPhotoCount >= MAX_PHOTOS_PER_ACCOUNT;

  const [mode, setMode] = useState<'camera' | 'upload' | 'preset'>('camera');
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  /**
   * Camera and location permission, checked before anything is started.
   * 'checking' -> nothing yet; 'ask' -> show our explanation and an Allow
   * button (the browser prompt appears only after that click); 'granted' ->
   * started; 'blocked' -> the browser has blocked it, so show how to unblock.
   */
  const [permState, setPermState] = useState<'checking' | 'ask' | 'granted' | 'blocked'>('checking');
  // Deliberately blank. The note is now printed onto the photo itself, so a
  // pre-filled sentence would stamp a claim the agent never made onto a
  // record the bank may rely on later.
  const [remark, setRemark] = useState<string>('');
  /**
   * The real reading, or null. Never a stand-in.
   *
   * These used to start at `account.latitude || 19.834521` -- a fixed point in
   * Chhatrapati Sambhajinagar -- with an accuracy of "4.2 m" already claimed
   * before the GPS had been asked. So a photo taken before the fix arrived, or
   * with location refused, carried coordinates that looked like a precise
   * survey reading and were invented.
   */
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [gpsError, setGpsError] = useState<string>('');
  const gpsWatchRef = useRef<number | null>(null);
  const gpsTimerRef = useRef<any>(null);
  const [liveDateTime, setLiveDateTime] = useState({
    date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-'),
    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
  });

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [capturedWatermarkedResult, setCapturedWatermarkedResult] = useState<any | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Update clock every few seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setLiveDateTime({
        date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-'),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
      });
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // On open: check permission status (this does not prompt). Start the camera
  // and GPS straight away only if both are already allowed. Otherwise wait for
  // the agent to press Allow, so the browser prompt follows a click.
  useEffect(() => {
    if (isOpen) {
      let cancelled = false;
      const readPermission = async (name: string): Promise<string> => {
        try {
          if (!navigator.permissions || !(navigator.permissions as any).query) return 'unknown';
          const s = await navigator.permissions.query({ name: name as any });
          return s.state;
        } catch {
          return 'unknown';
        }
      };
      (async () => {
        const cam = await readPermission('camera');
        const geo = await readPermission('geolocation');
        if (cancelled) return;
        if (cam === 'denied' || geo === 'denied') {
          setPermState('blocked');
        } else if (cam === 'granted' && geo === 'granted') {
          setPermState('granted');
          fetchRealGPS();
          startCamera('environment');
        } else {
          setPermState('ask');
        }
      })();
      return () => {
        cancelled = true;
        stopGpsWatch();
      };
    } else {
      stopCamera();
      stopGpsWatch();
      setPreviewImage(null);
      setCapturedWatermarkedResult(null);
      setGpsError('');
      setPermState('checking');
    }
    return () => stopGpsWatch();
  }, [isOpen]);

  /** Runs from the Allow button, so the browser prompts appear after the agent's click. */
  const allowCameraAndLocation = () => {
    setPermState('granted');
    fetchRealGPS();
    startCamera('environment');
  };

  const stopGpsWatch = () => {
    if (gpsWatchRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(gpsWatchRef.current);
      gpsWatchRef.current = null;
    }
    if (gpsTimerRef.current) {
      clearTimeout(gpsTimerRef.current);
      gpsTimerRef.current = null;
    }
  };

  /**
   * Wait for a GOOD fix rather than accepting the first one.
   *
   * Two things were wrong here.
   *
   * 1. On failure it invented coordinates: the customer's stored location
   *    jittered by `Math.random()` up to about 220 metres, stamped with a
   *    fabricated accuracy of 4.8 m. A refused permission produced a photo
   *    that looked like a precise survey reading of somewhere the agent had
   *    never stood. Nothing on the photo said so.
   *
   * 2. It took the FIRST position the phone offered. The first fix is usually
   *    the coarse network or cell one -- often hundreds of metres, sometimes
   *    kilometres -- and the accurate satellite fix arrives seconds later. So
   *    even a successful reading was frequently the wrong one, which is the
   *    "lat long is not correct" you saw.
   *
   * Now: watch for up to 12 seconds, keep the best accuracy seen, stop early
   * once it is within 25 m, and if nothing arrives, record NOTHING and say so.
   */
  const fetchRealGPS = () => {
    if (!navigator.geolocation) {
      setGpsError('This device has no location service.');
      return;
    }

    stopGpsWatch();
    setIsLocating(true);
    setGpsError('');

    let best: { lat: number; lng: number; acc: number } | null = null;
    const GOOD_ENOUGH_M = 25;
    const GIVE_UP_AFTER_MS = 12000;

    /**
     * THE REAL REASON, NOT THE GENERIC ONE.
     *
     * The error handler below set a specific message -- "Location permission
     * was refused" -- and then called `finish()`, which saw that there was no
     * reading yet and overwrote it with the generic "No GPS fix". So a
     * permission refusal and a genuine lack of signal looked IDENTICAL on
     * screen, and there was no way to tell which you were looking at.
     *
     * `finish()` no longer speaks over a message that already explains
     * itself.
     */
    let explained = false;

    const finish = () => {
      stopGpsWatch();
      setIsLocating(false);
      if (!best) {
        setLatitude(null);
        setLongitude(null);
        setGpsAccuracy(null);
        if (!explained) {
          setGpsError(
            'No GPS fix. The photo will be stamped GPS UNAVAILABLE rather than carrying a guessed location.'
          );
        }
      }
    };

    const accept = (pos: GeolocationPosition) => {
      const acc =
        typeof pos.coords.accuracy === 'number' && pos.coords.accuracy > 0
          ? pos.coords.accuracy
          : 9999;
      // Keep the best reading seen, not the latest.
      if (!best || acc < best.acc) {
        best = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc };
        setLatitude(best.lat);
        setLongitude(best.lng);
        setGpsAccuracy(best.acc);
      }
    };

    /**
     * THE SECOND ATTEMPT, DELIBERATELY COARSE.
     *
     * The first asks for a satellite fix and refuses anything cached. Indoors
     * -- and on plenty of phones outdoors -- that returns nothing at all, so
     * an agent standing in a borrower's shop got "GPS UNAVAILABLE" and no
     * location on the photo.
     *
     * So when the strict attempt comes back empty, the phone's own network
     * position is accepted instead: wifi and cell towers, usually hundreds of
     * metres out, sometimes a kilometre or more.
     *
     * IT IS NEVER PRESENTED AS PRECISE. The accuracy is recorded and stamped
     * on the photo alongside the coordinates, so a reading good to 1,200
     * metres says so. That is the whole difference between this and the
     * invented Sambhajinagar point that used to be written here: this is the
     * phone's real estimate of where it is, carrying its own margin of error,
     * rather than a number nobody measured.
     */
    const tryCoarse = () => {
      if (best) {
        finish();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          accept(pos);
          stopGpsWatch();
          setIsLocating(false);
          if (best) {
            setGpsError('');
          }
        },
        (err) => {
          console.warn('GPS coarse notice:', err?.message || err);
          if (err?.code === 1) {
            explained = true;
            setGpsError(
              'Location permission was refused for this site. The photo will be stamped GPS UNAVAILABLE. Allow location in the browser, and in the phone settings for the browser.'
            );
          } else if (err?.code === 2) {
            explained = true;
            setGpsError(
              'The phone could not work out where it is. Turn on Location in the phone settings -- and Google Location Accuracy -- then try again.'
            );
          }
          finish();
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
      );
    };

    gpsWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        accept(pos);
        if (best && best.acc <= GOOD_ENOUGH_M) {
          stopGpsWatch();
          setIsLocating(false);
        }
      },
      (err) => {
        console.warn('GPS notice:', err?.message || err);
        if (err?.code === 1) {
          // Permission is final: a coarse attempt would be refused too.
          explained = true;
          setGpsError(
            'Location permission was refused for this site. The photo will be stamped GPS UNAVAILABLE. Allow location in the browser, and in the phone settings for the browser.'
          );
          finish();
          return;
        }
        // Anything else -- no signal, timeout, service unavailable -- is worth
        // a coarse attempt before giving up.
        stopGpsWatch();
        tryCoarse();
      },
      { enableHighAccuracy: true, timeout: GIVE_UP_AFTER_MS, maximumAge: 0 }
    );

    gpsTimerRef.current = setTimeout(tryCoarse, GIVE_UP_AFTER_MS);
  };

  const startCamera = async (modeToUse: 'environment' | 'user' = facingMode) => {
    stopCamera();
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: modeToUse, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (videoRef.current) {
              const playPromise = videoRef.current.play();
              if (playPromise !== undefined) {
                playPromise.catch((err) => {
                  if (err?.name !== 'AbortError' && err?.name !== 'NotAllowedError') {
                    console.debug('Camera video play interrupted or suspended:', err);
                  }
                });
              }
            }
          };
        }
      }
    } catch {
      // Camera might not be permitted or available, switch to preset/upload gracefully
      setMode('preset');
    }
  };

  const toggleFacingMode = () => {
    const nextFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(nextFacing);
    startCamera(nextFacing);
    if (nextFacing === 'user') {
      setRemark((prev) => (prev.includes('Selfie') ? prev : `Agent Selfie / Field Verification at premises. ${prev}`));
    }
  };

  const stopCamera = () => {
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const captureSnapshot = () => {
    if (videoRef.current && videoRef.current.videoWidth) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        setPreviewImage(dataUrl);
        stopCamera();
      }
    } else {
      // Use preset simulated field photo
      useSimulatedFieldPhoto();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result as string);
        stopCamera();
      };
      reader.readAsDataURL(file);
    }
  };

  const useSimulatedFieldPhoto = () => {
    // Generate high quality canvas simulation of premises
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 900;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const grad = ctx.createLinearGradient(0, 0, 1200, 900);
      grad.addColorStop(0, '#f1f5f9');
      grad.addColorStop(0.5, '#e2e8f0');
      grad.addColorStop(1, '#cbd5e1');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1200, 900);

      // Building facade sketch
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(200, 180, 800, 500);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(250, 220, 200, 180);
      ctx.fillRect(520, 220, 200, 180);
      ctx.fillRect(780, 220, 160, 180);

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 24px sans-serif';
      ctx.fillText(`Customer Residence / Business Premises - ${account.customerName}`, 220, 140);
      ctx.font = '18px sans-serif';
      ctx.fillText(`${account.address}, ${account.city}`, 220, 170);

      setPreviewImage(canvas.toDataURL('image/jpeg', 0.9));
      stopCamera();
    }
  };

  const handleGenerateAndSave = async () => {
    if (!previewImage) {
      alert('Please take a photo or select an image first.');
      return;
    }

    // Saving is deliberately NOT blocked on a blank note. An agent in the
    // field should never be stopped by a form. A note left blank can be
    // filled in afterwards -- once -- from the photo itself.
    setIsProcessing(true);
    try {
      const photoRecord = await captureWatermarkedPhoto({
        imageSource: previewImage,
        accountId: account.accountId,
        date: liveDateTime.date,
        time: liveDateTime.time,
        latitude,
        longitude,
        accuracy: gpsAccuracy,
        remark,
        visitId,
        customerName: account.customerName,
      });

      setCapturedWatermarkedResult(photoRecord);
      if (onSuccess) onSuccess(photoRecord);
    } catch (err: any) {
      alert(`Error generating watermark: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden text-slate-800 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-600 rounded-xl text-white shadow-sm">
              <Camera className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <span>ScaleSupport Geo-Tagged Watermark Camera</span>
                  <span className="text-xs bg-blue-50 text-blue-700 font-mono font-bold px-2 py-0.5 rounded border border-blue-200">
                    {account.accountId}
                  </span>
                  {account.customerName && (
                    <span className="text-xs font-semibold text-slate-700 truncate max-w-[200px]">{account.customerName}</span>
                  )}
                </h2>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                <span>Automatic GPS Lat/Lng Stamping</span>
                <span>•</span>
                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                  <FileCheck className="w-3 h-3" />
                  Cloud Firestore Real-Time Optimization
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-xl transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/*
          The limit, said out loud before the photo is taken.

          An account keeps its two most recent photos. Nothing warned anybody
          about that, and a photo silently vanishing from an account is the
          kind of thing that gets reported as lost data weeks later, by which
          point nobody can explain it.
        */}
        {willReplaceOldest && !capturedWatermarkedResult && (
          <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-center gap-2 shrink-0">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
            <span>
              This customer already has{' '}
              <strong>{existingPhotoCount} photo{existingPhotoCount === 1 ? '' : 's'}</strong>. An
              account keeps its {MAX_PHOTOS_PER_ACCOUNT} most recent — saving this one will
              replace the oldest.
            </span>
          </div>
        )}

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {capturedWatermarkedResult ? (
            /* Success Watermark Preview screen */
            <div className="space-y-4 text-center">
              <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-center gap-2 text-emerald-800 text-sm font-semibold">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span>SRMS Watermarked Photo generated &amp; synced to Cloud Firestore!</span>
              </div>

              <div className="relative rounded-xl overflow-hidden border-2 border-blue-500 shadow-md bg-black max-h-[420px] flex items-center justify-center">
                <img
                  src={capturedWatermarkedResult.dataUrl}
                  alt="SRMS Watermarked Photo"
                  className="w-full h-auto max-h-[420px] object-contain"
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-left text-xs bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                <div>
                  <span className="text-slate-500 block font-medium">File Ref ID</span>
                  <span className="font-mono text-blue-700 font-bold">{capturedWatermarkedResult.driveFileId}</span>
                </div>
                <div>
                  <span className="text-slate-500 block font-medium">GPS Latitude</span>
                  <span className="font-mono text-emerald-700 font-bold">{typeof capturedWatermarkedResult.latitude === 'number' ? `${capturedWatermarkedResult.latitude.toFixed(6)}° N` : 'GPS unavailable'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block font-medium">GPS Longitude</span>
                  <span className="font-mono text-emerald-700 font-bold">{typeof capturedWatermarkedResult.longitude === 'number' ? `${capturedWatermarkedResult.longitude.toFixed(6)}° E` : '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block font-medium">Firestore Record</span>
                  <span className="text-amber-700 font-bold">Photos Collection ({account.accountId})</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setCapturedWatermarkedResult(null);
                    setPreviewImage(null);
                    startCamera();
                  }}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-xs rounded-xl border border-slate-200 cursor-pointer transition"
                >
                  Capture Another Photo
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer transition"
                >
                  Done &amp; View Timeline
                </button>
              </div>
            </div>
          ) : (
            /* Active Camera / Input Form */
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
              {permState !== 'granted' && permState !== 'checking' && (
                <div className="md:col-span-12">
                  {permState === 'blocked' ? (
                    <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-900 space-y-2">
                      <p className="font-bold">Camera or location is blocked for this site.</p>
                      <p>
                        Your browser will not ask again until you allow it. Click the lock icon
                        in the address bar, open <strong>Site settings</strong>, set <strong>Camera</strong> and{' '}
                        <strong>Location</strong> to <strong>Allow</strong>, then reload this page.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 space-y-3">
                      <p className="font-bold">Allow camera and location to take a geo-tagged photo</p>
                      <p>
                        The camera records the photo, and your location is stamped on it. Press
                        <strong> Allow</strong>, then choose <strong>Allow</strong> in the browser prompt.
                      </p>
                      <button
                        type="button"
                        onClick={allowCameraAndLocation}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl cursor-pointer transition"
                      >
                        Allow camera &amp; location
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* Left Column: Camera Viewport / Image Preview */}
              <div className="md:col-span-7 space-y-3">
                <div className="relative aspect-[4/3] bg-slate-900 rounded-xl overflow-hidden border border-slate-200 flex items-center justify-center">
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
                    />
                  )}

                  {/* Selfie / Flip Camera Floating Toggle */}
                  {!previewImage && (
                    <button
                      type="button"
                      onClick={toggleFacingMode}
                      className="absolute bottom-3 right-3 px-3 py-1.5 bg-slate-950/80 hover:bg-slate-900 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-md border border-slate-700/60 cursor-pointer backdrop-blur-xs transition z-10"
                      title="Switch between rear camera and front selfie camera"
                    >
                      <RefreshCw className="w-3.5 h-3.5 text-blue-400" />
                      <span>{facingMode === 'user' ? '🤳 Selfie (Active)' : '🤳 Selfie Mode'}</span>
                    </button>
                  )}

                  {/* On-screen GPS & Timestamp HUD */}
                  <div className="absolute top-2 left-2 right-2 flex items-center justify-between text-[11px] font-mono bg-slate-950/80 backdrop-blur-xs px-2.5 py-1 rounded-lg border border-slate-700/60 text-white">
                    <span className="flex items-center gap-1 text-emerald-400">
                      <MapPin className="w-3 h-3" />
                      {latitude === null || longitude === null
                        ? isLocating
                          ? 'Getting GPS fix…'
                          : 'GPS UNAVAILABLE'
                        : `${latitude.toFixed(4)}°N, ${longitude.toFixed(4)}°E${
                            gpsAccuracy === null
                              ? ''
                              : ` (±${gpsAccuracy < 10 ? gpsAccuracy.toFixed(1) : Math.round(gpsAccuracy)}m)`
                          }`}
                    </span>
                    <span className="text-slate-300">
                      {liveDateTime.date} {liveDateTime.time}
                    </span>
                  </div>

                  {/* Watermark target crosshair */}
                  {!previewImage && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 border border-red-500/70 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Camera Control Buttons */}
                <div className="flex items-center gap-2">
                  {!previewImage ? (
                    <button
                      type="button"
                      onClick={captureSnapshot}
                      className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 text-sm shadow-sm transition cursor-pointer"
                    >
                      <Camera className="w-4 h-4" />
                      <span>📸 Capture Snapshot</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setPreviewImage(null);
                        startCamera();
                      }}
                      className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl flex items-center justify-center gap-2 text-xs border border-slate-200 cursor-pointer transition"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      <span>Retake Photo</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl text-xs border border-slate-200 flex items-center gap-1.5 cursor-pointer transition"
                    title="Upload image file"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>Upload</span>
                  </button>

                  <button
                    type="button"
                    onClick={useSimulatedFieldPhoto}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl text-xs border border-slate-200 flex items-center gap-1.5 cursor-pointer transition"
                    title="Use simulated premises photo"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span>Sample Premise</span>
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>
              </div>

              {/* Right Column: Watermark Metadata & Agent Remark */}
              <div className="md:col-span-5 space-y-3.5 text-xs">
                {/* ScaleSupport Watermark Spec Box */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-blue-700 uppercase tracking-wider text-[11px]">
                      ScaleSupport Watermark Metadata
                    </span>
                    <button
                      type="button"
                      onClick={fetchRealGPS}
                      className="text-xs text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-1 cursor-pointer"
                      title="Refresh GPS"
                    >
                      <RefreshCw className={`w-3 h-3 ${isLocating ? 'animate-spin' : ''}`} />
                      <span>{isLocating ? 'Locating...' : 'Refresh GPS'}</span>
                    </button>
                  </div>

                  <div className="space-y-1 font-mono text-[11px] bg-white p-3 rounded-lg border border-slate-200 text-slate-700 shadow-2xs">
                    <p className="text-blue-700 font-bold">SCALESUPPORT • FIELD RECOVERY SYSTEM</p>
                    <p><span className="text-slate-400">Account ID:</span> <span className="text-slate-900 font-bold">{account.accountId}</span></p>
                    <p><span className="text-slate-400">Agent:</span> <span className="text-slate-900 font-bold">{currentUser.agentId || currentUser.username || currentUser.id} ({currentUser.name})</span></p>
                    <p><span className="text-slate-400">Date:</span> {liveDateTime.date} | <span className="text-slate-400">Time:</span> {liveDateTime.time}</p>
                    <p>
                      <span className="text-slate-400">Latitude:</span>{' '}
                      <span className={latitude === null ? 'text-amber-700 font-bold' : 'text-emerald-700 font-bold'}>
                        {latitude === null ? (isLocating ? 'locating…' : 'no fix') : `${latitude.toFixed(6)}° N`}
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-400">Longitude:</span>{' '}
                      <span className={longitude === null ? 'text-amber-700 font-bold' : 'text-emerald-700 font-bold'}>
                        {longitude === null ? (isLocating ? 'locating…' : 'no fix') : `${longitude.toFixed(6)}° E`}
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-400">Accuracy:</span>{' '}
                      <span
                        className={
                          gpsAccuracy === null
                            ? 'text-slate-500 font-bold'
                            : gpsAccuracy > 100
                            ? 'text-amber-700 font-bold'
                            : 'text-emerald-700 font-bold'
                        }
                        title="Anything over 100 m is too coarse to place someone at a door."
                      >
                        {gpsAccuracy === null
                          ? '—'
                          : `±${gpsAccuracy < 10 ? gpsAccuracy.toFixed(1) : Math.round(gpsAccuracy)} m`}
                      </span>
                    </p>
                    {gpsError ? (
                      <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-1">
                        {gpsError}
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* Quick-tap status. Sits ABOVE the note box so the agent can
                    log the common outcomes with one tap in the field. */}
                <div className="space-y-1.5">
                  <span className="text-slate-500 text-[11px] block font-medium">
                    Visit outcome (tap one):
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'Customer met',
                      'Customer absent',
                      'Shop closed',
                      'Refused to pay',
                      'Promised to pay',
                    ].map((label) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() =>
                          setRemark((prev) =>
                            prev.trim() ? `${label} — ${prev.trim()}` : label
                          )
                        }
                        className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-lg text-[11px] font-bold border border-blue-200 transition cursor-pointer"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Agent note input */}
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    Photo Note (saved with the record, not printed on the image):
                  </label>
                  <textarea
                    rows={3}
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder="Enter visit remarks, customer response, PTP amount commitment, premises status..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                {/* Quick Pre-fill chips */}
                <div className="space-y-1">
                  <span className="text-slate-500 text-[11px] block font-medium">Longer preset notes:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'Customer met personally. PTP given for ₹15,000.',
                      'Premises visited. Door locked, neighbour statement taken.',
                      'Customer agreed for split installment by month end.',
                      'Vehicle asset physical inspection completed.',
                    ].map((text, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setRemark(text)}
                        className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[10px] border border-slate-200 text-left transition cursor-pointer"
                      >
                        {text.slice(0, 32)}...
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit Action */}
                <div className="pt-2">
                  <button
                    type="button"
                    disabled={!previewImage || isProcessing}
                    onClick={handleGenerateAndSave}
                    className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition ${
                      previewImage && !isProcessing
                        ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                    }`}
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Applying Watermark &amp; Saving to Cloud Storage...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        <span>Apply Watermark &amp; Save to Cloud Storage</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
